import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@environment';
import { HttpListResponse } from '@ess-front/shared/models';
import { BookingSimpleBE } from '@app/utils/models/booking-simple-be.model';
import { BookingSimple } from '@app/utils/models/booking-simple.model';
import { PublicStatusType } from '@app/utils/models/public-status-type.model';
import { map, Observable } from 'rxjs';
import { BookingAlternative } from '@app/utils/models/booking-alternative.model';
import { BookingAlternativeBE } from '@app/utils/models/booking-alternative-be.model';

@Injectable({
  providedIn: 'root',
})
export class BookingApiService {
  private readonly apiURL = `${environment.apiEnv}api/v2/`;

  constructor(private httpClient: HttpClient) {}

  getTrips$(onlyRecent = false): Observable<BookingSimple[]> {
    const date30DaysAgo = this.getDate30DaysAgoISOString();

    const queryParams = onlyRecent ? `?limit=1000&end_date__gte=${date30DaysAgo}` : `?limit=1000`;
    return this.httpClient.get<HttpListResponse<BookingSimpleBE>>(`${this.apiURL}bookings/${queryParams}`).pipe(
      map(response => response.results),
      map(bookings => bookings.map((booking: BookingSimpleBE) => this.transformIntoBookingSimple(booking))),
    );
  }
  private getDate30DaysAgoISOString(): string {
    const date = new Date();
    date.setDate(date.getDate() - 30);
    return date.toISOString().split('T')[0];
  }

  private transformIntoBookingSimple(bookingSimpleBE: BookingSimpleBE): BookingSimple {
    const isTopLevel = !!bookingSimpleBE.top_level_group_title;
    return {
      ...this.transformIntoBookingSimpleAlternative(bookingSimpleBE),
      canNavigate: this.getCanNavigate(bookingSimpleBE),
      topLevelOptions: isTopLevel
        ? {
            alternatives: bookingSimpleBE.alternatives.map(alternative =>
              this.transformIntoBookingSimpleAlternative(alternative),
            ),
            title: bookingSimpleBE.top_level_group_title,
          }
        : null,
      member: {
        firstName: bookingSimpleBE.user.first_name,
        lastName: bookingSimpleBE.user.last_name,
      },
    };
  }

  // BookingAlternative is a subset of BookingSimple
  private transformIntoBookingSimpleAlternative(bookingAlternativeBE: BookingAlternativeBE): BookingAlternative {
    return {
      adults: bookingAlternativeBE.adults,
      bucketDestinations: bookingAlternativeBE.bucket_destinations.map(({ title }) => title),
      children: bookingAlternativeBE.children,
      conversationId: bookingAlternativeBE.conversation_id,
      destinations: bookingAlternativeBE.top_level_group_description,
      endDate: bookingAlternativeBE.end_date,
      hash: bookingAlternativeBE.hash,
      id: bookingAlternativeBE.id,
      image: bookingAlternativeBE.image,
      infants: bookingAlternativeBE.infants,
      opportunityDate: bookingAlternativeBE.opportunity_date,
      publicStatus: bookingAlternativeBE.public_status,
      summary: bookingAlternativeBE.summary,
      startDate: bookingAlternativeBE.start_date,
      statusType: bookingAlternativeBE.status,
      title: bookingAlternativeBE.title,
      totalTravelers: this.getTotalTravelers(bookingAlternativeBE),
      tripDuration: this.getTripDuration(bookingAlternativeBE),
    };
  }

  private getTotalTravelers({
    adults,
    children,
    infants,
  }: {
    adults: number;
    children: number;
    infants: number;
  }): number {
    return adults + infants + children;
  }

  private getTripDuration({ end_date, start_date }: BookingAlternativeBE): number | null {
    const MILLISECONDS_ON_A_DAY = 1000 * 60 * 60 * 24;
    if (!end_date || !start_date) return null;

    const end = new Date(end_date).valueOf();
    const start = new Date(start_date).valueOf();
    return Math.floor((end - start) / MILLISECONDS_ON_A_DAY + 1);
  }

  private getCanNavigate(bookingSimpleBE: BookingSimpleBE): boolean {
    if (bookingSimpleBE.alternatives.length) {
      return bookingSimpleBE.alternatives.every(
        alternative =>
          alternative.public_status.value !== PublicStatusType.FUTURE &&
          alternative.public_status.value !== PublicStatusType.BUILDING,
      );
    } else {
      return (
        bookingSimpleBE.public_status.value !== PublicStatusType.FUTURE &&
        bookingSimpleBE.public_status.value !== PublicStatusType.BUILDING
      );
    }
  }
}
